/**
 * ActionGrid Component
 *
 * Renders a grid of action buttons with optional row/column headers, without a
 * Card, panel title or timer. Used as the body of an `action_grid` form field,
 * so several grids can stack inside a single form panel.
 */

import React from "react"
import { ActionGridFieldDef, GridHeader } from "@/types/actions"
import { EmojiText } from "../low/AnimatedEmoji"
import { ActionButton } from "../ActionButton"

interface ActionGridProps {
  field: ActionGridFieldDef
  onActionClick: (actionId: string) => void | Promise<void>
  disabled?: boolean
  /** Action currently being submitted — may belong to a sibling grid. */
  pendingActionId?: string | null
}

/** Width of the row-header column, in units of one action column. */
const ROW_HEADER_FRACTION = 2.6

/**
 * Header cell — plain text, no button chrome.
 *
 * `break-words` lets long labels wrap inside their track instead of overflowing;
 * the track width itself is proportional, not content-driven (see
 * ROW_HEADER_FRACTION).
 *
 * With `label_hidden` the cell shows the icon alone, but the label still reaches the
 * DOM in `sr-only`: the icon is `aria-hidden`, and an `aria-label` on a plain `div`
 * with no role is ignored by screen readers.
 */
const HeaderCell: React.FC<{
  header: GridHeader
  row: number
  column: number
  align: "row" | "col"
}> = ({ header, row, column, align }) => (
  <div
    // min-w-0 defeats the `min-width: auto` of a grid item: without it a single long
    // word ignores the track width and spills over the neighbouring column.
    className={`flex min-w-0 items-center gap-1 text-[10px] leading-tight text-muted-foreground break-words ${
      align === "col" ? "justify-center text-center" : "justify-start text-left"
    }`}
    style={{ gridRow: row, gridColumn: column }}
  >
    {header.icon && (
      <span aria-hidden="true" className="text-xl leading-none">
        <EmojiText>{header.icon}</EmojiText>
      </span>
    )}
    <span className={header.label_hidden ? "sr-only" : undefined}>{header.label}</span>
  </div>
)

/**
 * ActionGrid - action buttons laid out on a rows x columns grid with headers.
 *
 * Cells are positioned explicitly (no auto-placement), so a sparse `cells` list
 * still lands exactly where the game mode asked — positional fidelity with the
 * TV board is the point of this field. `rows`/`columns` cover the action area
 * only; headers occupy extra tracks and shift cell placement by one.
 *
 * The grid renders even with an empty `cells` list: a section without available
 * actions must keep its headers, otherwise the stacked sections stop lining up.
 */
export const ActionGrid: React.FC<ActionGridProps> = ({
  field,
  onActionClick,
  disabled = false,
  pendingActionId = null,
}) => {
  const rowHeaders = field.row_headers ?? []
  const colHeaders = field.col_headers ?? []
  const hasRowHeaders = rowHeaders.length > 0
  const hasColHeaders = colHeaders.length > 0
  const rowOffset = hasColHeaders ? 2 : 1
  const colOffset = hasRowHeaders ? 2 : 1

  // Inline styles on purpose: Tailwind JIT cannot generate runtime-composed
  // class names like `grid-cols-${columns}`.
  //
  // The row-header column is proportional, not `auto`: an `auto` track is sized to
  // the label's max-content width (one line, spaces and all) before the `1fr` action
  // columns get what is left, so a long label would squeeze the buttons. It is wider
  // than one action column because a row header carries an icon next to its name. The
  // same ratio drives the SZPIEDZY board on the TV (`_render_section` in
  // szpiedzy/rendering.py), so the panel lines up with the board the player is already
  // looking at. The phone dossier deliberately stays narrower: its label font and icon
  // are fixed sizes, so its track does not have to grow the way the board's does.
  const gridTemplateColumns = hasRowHeaders
    ? `minmax(0, ${ROW_HEADER_FRACTION}fr) repeat(${field.columns}, minmax(0, 1fr))`
    : `repeat(${field.columns}, minmax(0, 1fr))`
  const gridTemplateRows = hasColHeaders
    ? `auto repeat(${field.rows}, minmax(0, auto))`
    : `repeat(${field.rows}, minmax(0, auto))`

  return (
    <div
      className="grid gap-1"
      data-testid={`action-grid-${field.id}`}
      style={{ gridTemplateColumns, gridTemplateRows }}
    >
      {colHeaders.map((header, index) => (
        <HeaderCell
          key={`col-${index}`}
          header={header}
          row={1}
          column={index + colOffset}
          align="col"
        />
      ))}

      {rowHeaders.map((header, index) => (
        <HeaderCell
          key={`row-${index}`}
          header={header}
          row={index + rowOffset}
          column={1}
          align="row"
        />
      ))}

      {field.cells.map((cell) => (
        <div
          key={`${cell.row}-${cell.col}`}
          className="grid"
          data-testid={`grid-cell-${field.id}-${cell.row}-${cell.col}`}
          style={{
            gridRow: cell.row + rowOffset,
            gridColumn: cell.col + colOffset,
          }}
        >
          <ActionButton
            action={cell.action}
            onClick={() => onActionClick(cell.action.id)}
            loading={pendingActionId === cell.action.id}
            disabled={disabled}
          />
        </div>
      ))}
    </div>
  )
}
